import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useTheme } from "../../hooks/useTheme";
import { applyChartDefaults, resolveChartRamps, type ChartSpec } from "./chartSpec";
import { buildChartTheme, chartRamps, type ChartMode, type ChartTokens } from "./chartTheme";
import { resolveThemeColor } from "./resolveThemeColor";

type EChartsModule = typeof import("echarts");
type EChartsInstance = ReturnType<EChartsModule["init"]>;

// ECharts is large; it loads the first time a reply carries a ```chart
// fence and is shared by every chart after that.
let echartsModule: Promise<EChartsModule> | null = null;
function loadECharts(): Promise<EChartsModule> {
  echartsModule ??= import("echarts").catch((cause: unknown) => {
    echartsModule = null;
    throw cause;
  });
  return echartsModule;
}

function readTokens(host: HTMLElement, mode: ChartMode): ChartTokens {
  const dark = mode === "dark";
  return {
    foreground: resolveThemeColor(host, "var(--color-foreground)", dark ? "#ffffff" : "#0b0b0b"),
    mutedForeground: resolveThemeColor(host, "var(--color-muted-foreground)", "#898781"),
    border: resolveThemeColor(host, "var(--color-border)", dark ? "#2c2c2a" : "#e1e0d9"),
    popover: resolveThemeColor(host, "var(--color-popover)", dark ? "#1a1a19" : "#fcfcfb"),
    popoverForeground: resolveThemeColor(
      host,
      "var(--color-popover-foreground)",
      dark ? "#ffffff" : "#0b0b0b",
    ),
    fontFamily: getComputedStyle(host).fontFamily || "system-ui, sans-serif",
  };
}

/**
 * A ```chart fence drawn by Apache ECharts: hover tooltips, legend toggles,
 * zoom where the option asks for it, and the interface's colours. The title
 * sits above the canvas in the interface's type, like the other figures.
 */
export function EChartsFigure({
  spec,
  mode,
  fill = false,
  onFailure,
}: {
  readonly spec: ChartSpec;
  readonly mode: ChartMode;
  /** Take the parent's height instead of the spec's (the floating window). */
  readonly fill?: boolean;
  readonly onFailure?: (cause: unknown) => void;
}) {
  const plotRef = useRef<HTMLDivElement | null>(null);
  // Custom themes share a mode but not colours: the chart is rebuilt when
  // the active theme changes as well as when light/dark flips.
  const { theme: themeId } = useTheme();
  // The instance lives in state so a re-init (theme switch) re-applies the
  // option even when the switch happens within one render.
  const [chart, setChart] = useState<EChartsInstance | null>(null);
  const onFailureRef = useRef(onFailure);
  useLayoutEffect(() => {
    onFailureRef.current = onFailure;
  }, [onFailure]);

  // One instance per theme: ECharts bakes the theme in at init.
  useLayoutEffect(() => {
    const host = plotRef.current;
    if (!host) return;
    let disposed = false;
    let observer: ResizeObserver | null = null;
    let instance: EChartsInstance | null = null;
    void loadECharts().then(
      (echarts) => {
        if (disposed) return;
        let created: EChartsInstance;
        try {
          const themeName = `amoreg-chat-${mode}`;
          echarts.registerTheme(themeName, buildChartTheme(mode, readTokens(host, mode)));
          created = echarts.init(host, themeName, { renderer: "canvas" });
        } catch (cause) {
          // A chart that cannot even start falls back to its source, like
          // one whose option ECharts rejects.
          onFailureRef.current?.(cause);
          return;
        }
        instance = created;
        setChart(created);
        if (typeof ResizeObserver !== "undefined") {
          observer = new ResizeObserver(() => created.resize());
          observer.observe(host);
        }
      },
      (cause: unknown) => onFailureRef.current?.(cause),
    );
    return () => {
      disposed = true;
      observer?.disconnect();
      instance?.dispose();
      setChart(null);
    };
  }, [mode, themeId]);

  useEffect(() => {
    if (!chart || chart.isDisposed()) return;
    try {
      const option = resolveChartRamps(applyChartDefaults(spec.option), chartRamps(mode));
      chart.setOption(option, { notMerge: true, lazyUpdate: true });
    } catch (cause) {
      // An option ECharts rejects (an unknown series type, a broken
      // dataset) falls back to the source view rather than a blank box.
      onFailureRef.current?.(cause);
    }
  }, [chart, mode, spec]);

  return (
    <div className="chat-markdown-echart" data-fill={fill ? "true" : undefined}>
      {spec.title ? <div className="chat-markdown-xychart-title">{spec.title}</div> : null}
      {spec.subtitle ? <div className="chat-markdown-xychart-caption">{spec.subtitle}</div> : null}
      <div
        ref={plotRef}
        className="chat-markdown-echart-plot"
        role="img"
        aria-label={spec.title ?? "Chart"}
        style={fill ? undefined : { height: spec.height }}
      />
    </div>
  );
}
